import { useMemo } from "react";
import { Sparkle } from "lucide-react";
import { useSelector } from "@/redux/store";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDiffDescription } from "./AuditTrail.utils";
import type { LineItemDiff } from "@rebuild/shared";

type Row = { roomName: string; lineItemName: string; diff: LineItemDiff; isComplementOrRecommendation: boolean };

export const AllAuditTrails = () => {
  const estimationDetail = useSelector(estimationSelector.estimationDetail);

  const rows = useMemo<Row[]>(() => {
    const all: Row[] = [];
    for (const detail of estimationDetail ?? []) {
      const roomName = detail.dataForUI?.roomName ?? detail.dataForUI?.metadata?.roomName ?? "";
      for (const tuple of detail.dataForUI?.page?.tuplesOutput ?? []) {
        for (const lineItem of tuple.lineItems ?? []) {
          const isComplementOrRecommendation = !!(lineItem.isComplement || lineItem.isLikely);
          for (const diff of lineItem.auditTrail ?? []) {
            all.push({ roomName, lineItemName: lineItem.lineItem, diff, isComplementOrRecommendation });
          }
        }
      }
    }
    all.sort((a, b) => b.diff.dateTime - a.diff.dateTime);
    return all;
  }, [estimationDetail]);

  return (
    <div className="p-4">
      <div className="border-border bg-muted/50 overflow-y-auto rounded-lg border">
        <Table className="[&_tr]:border-border/50">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="w-48 pr-4 font-semibold">Date</TableHead>
              <TableHead className="font-semibold">Room</TableHead>
              <TableHead className="font-semibold">Line Item</TableHead>
              <TableHead className="font-semibold">Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length ? (
              rows.map((row, i) => (
                <TableRow key={`${i}-${row.diff.dateTime}`}>
                  <TableCell className="w-8">
                    {row.isComplementOrRecommendation && <Sparkle className="h-4 w-4 text-blue-600" />}
                  </TableCell>
                  <TableCell className="text-muted-foreground w-48 pr-4">{new Date(row.diff.dateTime).toLocaleString()}</TableCell>
                  <TableCell>{row.roomName}</TableCell>
                  <TableCell>{row.lineItemName}</TableCell>
                  <TableCell>{formatDiffDescription(row.diff)}</TableCell>
                </TableRow>
              ))
            ) : (
              <>Audit Trail is empty</>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
